import { useCallback, useEffect } from 'react';
import { LruCache } from '@evergis/lru-cache';
import { useCache } from './hooks/useCache';
import { useWaitFor } from './WaitFor';
import { useResourceDebug } from './hooks/useResourceDebug';
export function createResource(fetch, { getKey = defaultGetKey, injected, cacheSize, name, debug, } = {}) {
    const cache = new LruCache(cacheSize);
    return {
        use: ({ input, waitInput, immediate = true, } = {}) => {
            const key = getKey(input);
            const cachedState = useCache(cache, key);
            const shouldWaitResource = useWaitFor();
            const shouldWait = shouldWaitResource || (!!waitInput && !input);
            const handleFetch = useCallback(async (force) => {
                const currentState = cache.get(key);
                if (!force &&
                    currentState !== undefined &&
                    isValidCache(currentState)) {
                    return currentState;
                }
                cache.set(key, {
                    data: currentState && currentState.data,
                    loading: true,
                    resolved: false,
                });
                try {
                    const { data, meta } = await fetch(input)(injected);
                    const result = { data, meta, loading: false, resolved: true };
                    cache.set(key, result);
                    return result;
                }
                catch (error) {
                    const result = { error, loading: false, resolved: false };
                    cache.set(key, result);
                    return result;
                }
            }, [key]);
            const refetch = useCallback(() => handleFetch(true), [key]);
            useEffect(() => {
                if (immediate && !shouldWait) {
                    handleFetch();
                }
            }, [immediate, shouldWait, key, cachedState && cachedState.invalidated]);
            useResourceDebug({
                state: { loading: true, resolved: false, ...cachedState },
                shouldWait,
                key,
                name,
                enableLog: debug,
            });
            return { loading: true, resolved: false, ...cachedState, refetch };
        },
        useCache: (input) => {
            const key = getKey(input);
            return useCache(cache, key);
        },
        invalidate: (input) => {
            const key = getKey(input);
            const state = cache.get(key);
            if (state) {
                cache.set(key, { ...state, invalidated: true });
            }
        },
        invalidateAll: () => {
            cache.entries().forEach(([key, state]) => {
                if (state) {
                    cache.set(key, { ...state, invalidated: true });
                }
            });
        },
        read: ({ input, condition }) => {
            if (input) {
                const key = getKey(input);
                const state = cache.get(key);
                return state && state.data;
            }
            if (condition) {
                const entry = cache.entries().find(([, state]) => {
                    return condition(state.data);
                });
                return entry && entry[1].data;
            }
            return;
        },
        write: ({ input, shouldUpdate, reducer }) => {
            if (input) {
                const key = getKey(input);
                const state = cache.get(key);
                if (!shouldUpdateCache(state) || !state)
                    return;
                const data = reducer(state.data);
                if (!data) {
                    cache.delete(key);
                }
                else {
                    cache.set(key, { ...state, data });
                }
                return;
            }
            cache.entries().forEach(([key, state]) => {
                if (!shouldUpdateCache(state) ||
                    (shouldUpdate && !shouldUpdate(state.data)))
                    return;
                const data = reducer(state.data);
                if (!data) {
                    cache.delete(key);
                }
                else {
                    cache.set(key, { ...state, data });
                }
            });
        },
        clear: () => cache.clear(),
    };
}
function defaultGetKey(input) {
    return input ? JSON.stringify(input) : 'key';
}
function isValidCache(state) {
    return !state.invalidated && !state.error;
}
function shouldUpdateCache(state) {
    return (!!state &&
        !state.loading &&
        !state.error &&
        !state.invalidated &&
        !!state.resolved);
}
//# sourceMappingURL=createResource.js.map